import React, { useEffect, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApiService from "../../../utils/fetch";
import {
  setCustomManageForecastTreeData,
  setCustomManageForecastBottlerData,
  setCustomManageForecastFinalTreeData,
} from "../../../redux/customEntitySlice";
import { useCustomBottler } from "../../../context/CustomBottlerContext";

const CustomBottlerSelectionAccordion = ({
  selectedPeriod,
  selectedTerm,
  hasSameSubmission,
}) => {
  const [customSelectedIds, setCustomSelectedIds] = useState([]);
  const [customTreeData, setCustomTreeData] = useState([]);
  const [customFilteredTreeData, setCustomFilteredTreeData] = useState([]);
  const [customSearchQuery, setCustomSearchQuery] = useState("");
  const dispatch = useDispatch();
  const { setCustomSelectedData } = useCustomBottler();
  const customManageForecastTreeDataRedux = useSelector(
    (state) => state.customEntity.customManageForecastTreeData,
  );

  // Event handler for when selected items change
  const handleCustomSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineCustomIdsToSet(
      customFilteredTreeData,
      ids,
      customSelectedIds,
    );

    setCustomSelectedIds(newSelectedIds);

    const filteredItems = newSelectedIds.filter((item) =>
      item.startsWith("plant"),
    );
    const applyTreeViewData = filterCustomBottlersByPlant(
      filteredItems,
      customTreeData,
    );

    const bottler = getBottlerForFirstCustomer(newSelectedIds, customTreeData);
    dispatch(setCustomManageForecastBottlerData(bottler));

    setCustomSelectedData(applyTreeViewData);
  };

  const getBottlerForFirstCustomer = (selectedIds, treeData) => {
    // 1. Prefer explicit customer selection
    const customerId = selectedIds.find((id) => id.startsWith("customer"));

    // 2. Otherwise infer from a plant
    const plantId =
      customerId == null
        ? selectedIds.find((id) => id.startsWith("plant"))
        : null;

    if (!customerId && !plantId) return null;

    for (const bottler of treeData) {
      for (const customer of bottler.children || []) {
        // Customer explicitly selected
        if (customer.id === customerId) {
          return bottler.label;
        }

        // Plant selected → infer customer
        if (plantId) {
          for (const plant of customer.children || []) {
            if (plant.id === plantId) {
              return bottler.label;
            }
          }
        }
      }
    }

    return null;
  };

  const transformCustomData = (data) => {
    return data.map((bottler, bottlerIndex) => ({
      id: `bottler${bottlerIndex + 1}`,
      label: bottler.bottler,
      children: bottler.customers.map((customer, customerIndex) => ({
        id: `customer${bottlerIndex + 1}-${customerIndex + 1}`,
        label: `${customer.customerID}`,
        customerID: customer.customerID,
        children: customer.locationInfoList.map(
          (locationInfo, locationIndex) => ({
            id: `plant${bottlerIndex + 1}-${customerIndex + 1}-${
              locationIndex + 1
            }`,
            label: ` ${locationInfo.location}`,
            locationId: locationInfo.location,
            shortTermPeriodList: locationInfo.applicableShortTermPeriodList,
            longTermPeriodList: locationInfo.applicableLongTermPeriodList,
          }),
        ),
      })),
    }));
  };

  // useEffect(() => {
  //     if (
  //         customManageForecastTreeDataRedux === null ||
  //         customManageForecastTreeDataRedux.length === 0
  //     ) {
  //         const getData = async () => {
  //             try {
  //                 const response = await ApiService.get("v1/manage-forecast/bottler-selection/custom");

  //                 const bottler = response.data[0]?.bottler;
  //                 dispatch(setCustomManageForecastBottlerData(bottler));

  //                 const transformedData = transformCustomData(response.data);
  //                 setCustomTreeData(transformedData);
  //                 setCustomFilteredTreeData(transformedData);

  //                 const allIds = getCustomAllIds(transformedData);
  //                 dispatch(setCustomManageForecastTreeData(transformedData));
  //                 const filteredItems = allIds.filter(item => item.startsWith("plant"));
  //                 const applyTreeViewData = filterCustomBottlersByPlant(filteredItems, transformedData);
  //                 dispatch(setCustomManageForecastFinalTreeData(applyTreeViewData));
  //             } catch (error) {
  //                 console.error("Error fetching custom bottler data", error);
  //             }
  //         };
  //         getData();
  //     } else {
  //         setCustomTreeData(customManageForecastTreeDataRedux);
  //         setCustomFilteredTreeData(customManageForecastTreeDataRedux);
  //     }
  // }, []);

  // useEffect(() => {
  //     filterCustomTreeDataBasedOnPeriodTerm(selectedPeriod, selectedTerm);
  // }, [selectedPeriod, selectedTerm]);

  const refreshData = async () => {
    try {
      const response = await ApiService.get(
        "v1/manage-forecast/bottler-selection/custom",
      );

      // const bottler = response.data[0]?.bottler;
      // dispatch(setCustomManageForecastBottlerData(bottler));

      const transformedData = transformCustomData(response.data);
      setCustomTreeData(transformedData);
      setCustomFilteredTreeData(transformedData);

      const allIds = getCustomAllIds(transformedData);
      dispatch(setCustomManageForecastTreeData(transformedData));
      const filteredItems = allIds.filter((item) => item.startsWith("plant"));
      const applyTreeViewData = filterCustomBottlersByPlant(
        filteredItems,
        transformedData,
      );
      dispatch(setCustomManageForecastFinalTreeData(applyTreeViewData));

      // Reapply filtering based on selectedPeriod and selectedTerm
      filterCustomTreeDataBasedOnPeriodTerm(
        selectedPeriod,
        selectedTerm,
        transformedData,
      );
    } catch (error) {
      console.error("Error fetching custom bottler data:", error);
    }
  };

  // Run on mount if Redux data is empty
  useEffect(() => {
    if (
      customManageForecastTreeDataRedux === null ||
      customManageForecastTreeDataRedux.length === 0
    ) {
      refreshData();
    } else {
      setCustomTreeData(customManageForecastTreeDataRedux);
      setCustomFilteredTreeData(customManageForecastTreeDataRedux);
      // Apply filtering on mount if period and term are set
      filterCustomTreeDataBasedOnPeriodTerm(
        selectedPeriod,
        selectedTerm,
        customManageForecastTreeDataRedux,
      );
    }
  }, []);

  // Re-filter when selectedPeriod or selectedTerm changes
  useEffect(() => {
    if (customTreeData && customTreeData.length > 0) {
      filterCustomTreeDataBasedOnPeriodTerm(
        selectedPeriod,
        selectedTerm,
        customTreeData,
      );
    }
  }, [selectedPeriod, selectedTerm]);

  const filterCustomTreeDataBasedOnPeriodTerm = (
    selectedPeriod,
    selectedTerm,
    data,
  ) => {
    if (hasSameSubmission) return;

    const isValid = selectedPeriod && selectedTerm;
    const periodKey = selectedPeriod?.split("-").slice(0, 3).join("-");

    setCustomSelectedIds([]);
    setCustomSelectedData([]);

    if (!isValid) {
      setCustomFilteredTreeData(data);
      setCustomTreeData(data);
      return;
    }

    const isShortTerm = selectedTerm === "Short Term";
    const listKey = isShortTerm ? "shortTermPeriodList" : "longTermPeriodList";

    const filteredTree = data
      .map((bottler) => {
        const filteredCustomers = bottler.children
          .map((customer) => {
            const filteredPlants = customer.children.filter((plant) =>
              plant[listKey]?.some((p) => p.startsWith(periodKey)),
            );
            return filteredPlants.length > 0
              ? { ...customer, children: filteredPlants }
              : null;
          })
          .filter(Boolean);

        return filteredCustomers.length > 0
          ? { ...bottler, children: filteredCustomers }
          : null;
      })
      .filter(Boolean);

    setCustomFilteredTreeData(filteredTree);
    setCustomTreeData(filteredTree);
  };

  const getCustomAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getCustomAllIds(item.children));
      }
    });
    return ids;
  };

  const filterCustomBottlersByPlant = (plantIds, products) => {
    return products
      .map((bottler) => {
        const filteredCustomers = bottler.children
          .map((customer) => {
            const matchingPlants = customer.children.filter((plant) =>
              plantIds.includes(plant.id),
            );
            return matchingPlants.length > 0
              ? {
                  customerId: customer.customerID.split("-")[0],
                  locationId: matchingPlants.map(
                    (plant) => plant.locationId.split("-")[0],
                  ),
                }
              : null;
          })
          .filter(Boolean);

        return filteredCustomers.length > 0
          ? { bottler: bottler.label, customerList: filteredCustomers }
          : null;
      })
      .filter(Boolean)
      .flatMap((bottler) =>
        bottler.customerList.flatMap((customer) =>
          customer.locationId.map((locationId) => ({
            locationId,
            customerId: customer.customerId,
          })),
        ),
      );
  };

  function determineCustomIdsToSet(items, newIds, currentIds) {
    const isDeselectingNode = currentIds.length > newIds.length;
    if (isDeselectingNode) {
      const removed = currentIds.filter((id) => !newIds.includes(id))[0];
      const parentIdsToRemove = getCustomAllParentIds(items, removed);
      const childIdsToRemove = getCustomSelectedIdsAndChildrenIds(items, [
        removed,
      ]);
      const newIdsWithParentsAndChildrenRemoved = newIds.filter(
        (id) =>
          !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
      );
      return newIdsWithParentsAndChildrenRemoved;
    }
    const added = newIds.filter((id) => !currentIds.includes(id))[0];
    const idsToSet = getCustomSelectedIdsAndChildrenIds(items, newIds);
    let parent = getCustomParentNode(items, added);
    while (parent) {
      const childIds = parent.children?.map((node) => node.id) ?? [];
      const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));
      if (allChildrenSelected) {
        idsToSet.push(parent.id);
        parent = getCustomParentNode(items, parent.id);
      } else {
        break;
      }
    }
    return idsToSet;
  }

  function getCustomSelectedIdsAndChildrenIds(items, selectedIds) {
    const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
    for (const item of items) {
      if (selectedIds.includes(item.id)) {
        selectedIdIncludingChildrenIds.add(item.id);
        if (item.children) {
          const childrenIds = item.children.map((child) => child.id);
          const childrenSelectedIds = getCustomSelectedIdsAndChildrenIds(
            item.children,
            childrenIds,
          );
          childrenSelectedIds.forEach((selectedId) =>
            selectedIdIncludingChildrenIds.add(selectedId),
          );
        }
      } else if (item.children) {
        const childrenSelectedIds = getCustomSelectedIdsAndChildrenIds(
          item.children,
          selectedIds,
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId),
        );
      }
    }
    return [...Array.from(selectedIdIncludingChildrenIds)];
  }

  function getCustomParentNode(items, id) {
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child) => child.id === id)) {
          return item;
        } else {
          const parentNode = getCustomParentNode(item.children, id);
          if (parentNode) {
            return parentNode;
          }
        }
      }
    }
    return undefined;
  }

  function getCustomAllParentIds(items, id) {
    const parentIds = [];
    let parent = getCustomParentNode(items, id);
    while (parent) {
      parentIds.push(parent.id);
      parent = getCustomParentNode(items, parent.id);
    }
    return parentIds;
  }

  const handleCustomSearchChange = (event) => {
    setCustomSearchQuery(event.target.value);
    setCustomFilteredTreeData(
      filterCustomTreeData(customTreeData, event.target.value),
    );
  };

  const filterCustomTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterCustomTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        // If parent matches query, include ALL children (unfiltered)
        // If parent doesn't match, only include children that match
        const children = matches
          ? item.children
          : item.children ? filterCustomTreeData(item.children, query) : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "75vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: "-20px",
            width: "100%",
          }}
        >
          <Typography gutterBottom style={{ marginBottom: "-10px" }}>
            Bottler Selection *
          </Typography>
          <IconButton
            onClick={refreshData}
            size="small"
            title="Refresh Bottler Selection"
            aria-label="refresh bottler selection"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search"
          margin="normal"
          value={customSearchQuery}
          onChange={handleCustomSearchChange}
          sx={{
            "& .MuiInputBase-input": {
              padding: "6px 8px",
              fontSize: "0.85rem",
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton size="small">
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, pt: 0 }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            selectedItems={customSelectedIds}
            onSelectedItemsChange={handleCustomSelectedItemsChange}
            items={customFilteredTreeData}
            sx={{
              flexGrow: 1,
              backgroundColor: "white",
              "& .MuiTreeItem-label": {
                fontSize: "11px",
              },
            }}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default CustomBottlerSelectionAccordion;
